/**
 * Judgement (Oh Hell) — server-authoritative rules engine (4 players, every player for themselves).
 *
 * Rules implemented (toggles live in {@link JudgementConfig}):
 *  - 4 players, one 52-card deck. The number of cards dealt changes every round: 1, 2, ... `maxCards`, then back down to 1
 *    (so `maxCards` 7 is 13 rounds). The top card of the rest of the deck is turned face up; its suit is trump.
 *  - Starting left of the dealer, each player bids how many tricks they will win (0..cards). When `hook` is on, the dealer
 *    may not bid the number that makes the bids add up to the number of tricks, so somebody is always wrong.
 *  - The player left of the dealer leads. Players must follow the led suit when able; the highest trump wins the trick,
 *    otherwise the highest card of the led suit. The trick winner leads next.
 *  - Scoring per round: win exactly your bid and you score 10 + the bid; miss it and you lose the size of the miss
 *    (when `penalizeMiss` is on, otherwise you score nothing).
 *  - The match always plays every round; the highest total wins and equal totals share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkBool, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface JudgementConfig {
  /** Largest hand dealt; the match plays 1..maxCards..1. */
  maxCards: number;
  /** The dealer may not bid the number that makes the total bids equal the tricks on offer. */
  hook: boolean;
  /** A missed bid loses the size of the miss (otherwise it just scores 0). */
  penalizeMiss: boolean;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
}

export type JudgementPhase = 'BIDDING' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface JudgementTrickPlay {
  seat: number;
  card: CardId;
}

export interface JudgementCompletedTrick {
  leader: number;
  plays: JudgementTrickPlay[];
  winner: number;
}

export interface JudgementRoundResult {
  cards: number;
  trump: string;
  bids: number[];
  won: number[];
  /** Points each seat scored this round. */
  scores: number[];
}

export interface JudgementState {
  config: JudgementConfig;
  players: string[];
  round: number;
  /** Cards dealt to each player this round. */
  cards: number;
  dealer: number;
  phase: JudgementPhase;
  hands: CardId[][];
  /** The face-up card that names trump. */
  turnCard: CardId;
  trump: string;
  bids: (number | null)[];
  turn: number;
  trick: JudgementTrickPlay[];
  tricks: JudgementCompletedTrick[];
  tricksWon: number[];
  totals: number[];
  roundResults: JudgementRoundResult[];
}

export type JudgementAction = { type: 'bid'; value: number } | { type: 'play'; card: CardId };

export type JudgementLegal =
  | { kind: 'bid'; min: number; max: number; /** The one bid the dealer may not make, or null. */ forbidden: number | null }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface JudgementView {
  gameType: 'JUDGEMENT';
  config: JudgementConfig;
  seat: number | null;
  phase: JudgementPhase;
  round: number;
  totalRounds: number;
  cards: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  turnCard: CardId;
  trump: string;
  bids: (number | null)[];
  tricksWon: number[];
  trick: JudgementTrickPlay[];
  lastTrick: JudgementCompletedTrick | null;
  tricks: JudgementCompletedTrick[];
  roundResults: JudgementRoundResult[];
  totals: number[];
}

const DEFAULTS: JudgementConfig = {
  maxCards: 7,
  hook: true,
  penalizeMiss: true,
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;

/** Cards dealt in a given round: 1, 2, ... maxCards, ... 2, 1. */
export function judgementCardsForRound(round: number, maxCards: number): number {
  return round <= maxCards ? round : 2 * maxCards - round;
}

export const judgementTotalRounds = (maxCards: number): number => 2 * maxCards - 1;

/** Exact bid scores 10 + bid; a miss loses the size of the miss (or 0). */
export function judgementScore(bid: number, won: number, penalizeMiss: boolean): number {
  if (bid === won) return 10 + bid;
  return penalizeMiss ? -Math.abs(bid - won) : 0;
}

/** Highest trump wins; otherwise the highest card of the led suit. */
export function judgementTrickWinner(plays: readonly JudgementTrickPlay[], trump: string): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  const beats = (c: ReturnType<typeof parseCard>, b: ReturnType<typeof parseCard>): boolean => {
    if (c.suit === b.suit) return c.rank > b.rank;
    return c.suit === trump && b.suit !== trump;
  };
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    if ((c.suit === lead || c.suit === trump) && beats(c, b)) best = p;
  }
  return best.seat;
}

export function judgementLegalCards(hand: readonly CardId[], trick: readonly JudgementTrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  return followers.length > 0 ? followers : [...hand];
}

/** The bid the dealer is barred from making, or null when it does not apply. */
export function judgementForbiddenBid(state: Pick<JudgementState, 'bids' | 'dealer' | 'cards' | 'config'>, seat: number): number | null {
  if (!state.config.hook || seat !== state.dealer) return null;
  const others = state.bids.reduce<number>((n, b, s) => (s === seat ? n : n + (b ?? 0)), 0);
  const forbidden = state.cards - others;
  return forbidden >= 0 && forbidden <= state.cards ? forbidden : null;
}

function deal(state: JudgementState, rng: Rng, events: EngineEvent[]): void {
  state.cards = judgementCardsForRound(state.round, state.config.maxCards);
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  for (let i = 0; i < state.cards * SEATS; i++) hands[i % SEATS]!.push(deck[i]!);
  state.hands = hands.map((h) => sortHand(h));
  state.turnCard = deck[state.cards * SEATS]!;
  state.trump = parseCard(state.turnCard).suit;
  state.bids = [null, null, null, null];
  state.tricks = [];
  state.trick = [];
  state.tricksWon = [0, 0, 0, 0];
  state.phase = 'BIDDING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, cards: state.cards, trump: state.trump, turnCard: state.turnCard });
}

export const judgementEngine: GameEngine<JudgementConfig, JudgementState, JudgementAction, JudgementView, JudgementLegal> = {
  gameType: 'JUDGEMENT',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxCards', c.maxCards, 1, 12);
    checkBool('hook', c.hook);
    checkBool('penalizeMiss', c.penalizeMiss);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Judgement needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: JudgementState = {
      config,
      players,
      round: 1,
      cards: 1,
      dealer: rng.int(SEATS),
      phase: 'BIDDING',
      hands: [],
      turnCard: '',
      trump: 'S',
      bids: [null, null, null, null],
      turn: 0,
      trick: [],
      tricks: [],
      tricksWon: [0, 0, 0, 0],
      totals: [0, 0, 0, 0],
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'bid' && typeof raw.value === 'number' && Number.isInteger(raw.value) && raw.value >= 0 && raw.value <= 12) {
      return { type: 'bid', value: raw.value };
    }
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Judgement action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'BIDDING' || state.phase === 'PLAYING'), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'bid') {
      assertEngine(state.phase === 'BIDDING', 'INVALID_PHASE', 'Not bidding now');
      assertEngine(action.value >= 0 && action.value <= state.cards, 'INVALID_AMOUNT', `Bid between 0 and ${state.cards}`);
      assertEngine(action.value !== judgementForbiddenBid(state, seat), 'ILLEGAL_MOVE', 'The dealer cannot make the bids add up to the tricks');
      state.bids[seat] = action.value;
      events.push({ type: 'bid', seat, value: action.value });
      if (state.bids.every((b) => b !== null)) {
        state.phase = 'PLAYING';
        state.turn = (state.dealer + 1) % SEATS;
        events.push({ type: 'playStart', leader: state.turn });
      } else {
        state.turn = (seat + 1) % SEATS;
      }
      return events;
    }

    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(judgementLegalCards(hand, state.trick).includes(action.card), 'ILLEGAL_MOVE', 'You must follow the led suit');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = judgementTrickWinner(state.trick, state.trump);
    state.tricksWon[winner]! += 1;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BIDDING' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'BIDDING') return { kind: 'bid', min: 0, max: state.cards, forbidden: judgementForbiddenBid(state, seat) };
    if (state.phase === 'PLAYING') return { kind: 'play', cards: judgementLegalCards(state.hands[seat]!, state.trick) };
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BIDDING') {
      const forbidden = judgementForbiddenBid(state, seat);
      // Bid 0 (or 1 when 0 is barred): the cheapest commitment that is still legal.
      return { type: 'bid', value: forbidden === 0 ? 1 : 0 };
    }
    const legal = judgementLegalCards(state.hands[seat]!, state.trick);
    const best = [...legal].sort((a, b) => parseCard(a).rank - parseCard(b).rank)[0]!;
    return { type: 'play', card: best };
  },

  getView(state, seat) {
    return {
      gameType: 'JUDGEMENT',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      totalRounds: judgementTotalRounds(state.config.maxCards),
      cards: state.cards,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      turnCard: state.turnCard,
      trump: state.trump,
      bids: [...state.bids],
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      roundResults: state.roundResults.map((r) => ({ ...r, bids: [...r.bids], won: [...r.won], scores: [...r.scores] })),
      totals: [...state.totals],
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < state.cards) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const bids = state.bids.map((b) => b ?? 0);
      const scores = bids.map((b, s) => judgementScore(b, state.tricksWon[s]!, state.config.penalizeMiss));
      state.roundResults.push({ cards: state.cards, trump: state.trump, bids, won: [...state.tricksWon], scores });
      state.totals = state.totals.map((t, s) => t + scores[s]!);
      events.push({ type: 'roundEnd', round: state.round, bids, won: [...state.tricksWon], scores, totals: [...state.totals] });
      if (state.round >= judgementTotalRounds(state.config.maxCards)) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: standingsFromScores(state.totals.map((score, seat) => ({ seat, score }))) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standingsFromScores(state.totals.map((score, seat) => ({ seat, score })));
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};
